import { useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, Area, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ReferenceArea, ReferenceDot,
} from "recharts";
import { axisINR } from "../../../lib/chartSeries";
import { ChartTooltip } from "./ChartTooltip";

const OPACITY = { Paid: 1, Partial: 0.8, Future: 0.6 };
const isFinePointer = () => {
  try { return window.matchMedia("(pointer: fine)").matches; } catch { return true; }
};

function EventDot({ cx, cy, ev }) {
  if (cx == null || cy == null) return null;
  const rate = ev.kind === "rate";
  return (
    <g data-testid={`chart-event-${ev.kind}`}>
      {rate
        ? <rect x={cx - 5} y={cy - 5} width={10} height={10} transform={`rotate(45 ${cx} ${cy})`} fill="#C9A227" stroke="#fff" strokeWidth={1.5} />
        : <circle cx={cx} cy={cy} r={6} fill="#2E9E6B" stroke="#fff" strokeWidth={1.5} />}
      <title>{ev.text}</title>
    </g>
  );
}

function Patterns({ uid, drawn }) {
  return (
    <defs>
      {drawn.map((d) => (
        <pattern key={d.key} id={`${uid}-pat-${d.key}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill={d.color} />
          {d.hatch && <line x1="0" y1="0" x2="0" y2="6" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="2" />}
        </pattern>
      ))}
      {drawn.map((d) => (
        <linearGradient key={`g${d.key}`} id={`${uid}-grad-${d.key}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={d.color} stopOpacity="0.85" />
          <stop offset={d.boundary} stopColor={d.color} stopOpacity="0.85" />
          <stop offset={d.boundary} stopColor={d.color} stopOpacity="0.3" />
          <stop offset="1" stopColor={d.color} stopOpacity="0.3" />
        </linearGradient>
      ))}
    </defs>
  );
}

function renderSeries(type, d, data, uid, hidden, axisOf) {
  const common = { isAnimationActive: false, hide: hidden.has(d.key), yAxisId: axisOf(d.key), name: d.label };
  const asLine = type === "line" || ["balance", "emi"].includes(d.key) || (type === "area" && d.key === "prepayment");
  if (asLine) {
    return [
      <Line key={`${d.key}p`} {...common} dataKey={`v_${d.key}__p`} stroke={d.color} strokeWidth={2.25}
            strokeDasharray={d.dash || undefined} dot={false} connectNulls={false} />,
      <Line key={`${d.key}f`} {...common} dataKey={`v_${d.key}__f`} stroke={d.color} strokeWidth={2}
            strokeDasharray={d.fdash || "2 3"} strokeOpacity={0.75} dot={false} connectNulls={false} legendType="none" />,
    ];
  }
  if (type === "area") {
    return [<Area key={d.key} {...common} type="monotone" dataKey={`v_${d.key}`} stackId="s"
                  stroke={d.color} strokeDasharray={d.dash || undefined} fill={`url(#${uid}-grad-${d.key})`} />];
  }
  return [
    <Bar key={d.key} {...common} dataKey={`v_${d.key}`} stackId={type === "grouped" ? undefined : "s"}
         fill={`url(#${uid}-pat-${d.key})`} maxBarSize={36}>
      {data.map((r) => <Cell key={r.label} fillOpacity={OPACITY[r.status] ?? 1} />)}
    </Bar>,
  ];
}

export function LoanChart({ data, drawn, settings, hidden, markers, colors, mode, loanNames, onZoom, uid, tooltip }) {
  const [drag, setDrag] = useState({ a: null, b: null });
  const pct = settings.display === "pct";
  const hasRight = !pct && drawn.some((d) => d.key === "balance") && drawn.length > 1;
  const axisOf = (k) => (hasRight && k === "balance" ? "right" : "left");
  const labels = new Set(data.map((r) => r.label));
  const firstKey = drawn.find((d) => !hidden.has(d.key) && d.key !== "balance")?.key;
  const tick = { fontSize: 10, fill: colors.fg };
  const lastPaid = data.reduce((acc, r, i) => (r.status === "Paid" ? i : acc), -1);
  const boundary = data.length > 1 ? Math.max(0, lastPaid) / (data.length - 1) : 1;
  const withB = drawn.map((d) => ({ ...d, boundary: lastPaid < 0 ? 0 : boundary }));
  const fine = isFinePointer();
  const showCross = markers.crossover && markers.crossover.month > 1 && ["line", "area", "combo"].includes(settings.chartType) && labels.has(markers.crossover.label);

  return (
    <div className="h-[300px] md:h-[340px] select-none [&_*:focus:not(:focus-visible)]:outline-none" data-testid="custom-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          key={`${data[0]?.label}-${data.length}`}
          data={data} margin={{ top: 18, right: hasRight ? 4 : 12, left: 0, bottom: 4 }}
          onMouseDown={(e) => fine && e?.activeLabel && setDrag({ a: e.activeLabel, b: null })}
          onMouseMove={(e) => drag.a && e?.activeLabel && setDrag((x) => ({ ...x, b: e.activeLabel }))}
          onMouseUp={() => { if (drag.a && drag.b && drag.a !== drag.b) onZoom(drag.a, drag.b); setDrag({ a: null, b: null }); }}
        >
          <Patterns uid={uid} drawn={withB} />
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={tick} stroke={colors.grid} minTickGap={14} interval="preserveStartEnd" />
          <YAxis yAxisId="left" tick={tick} stroke={colors.grid} width={pct ? 40 : 58}
                 tickFormatter={(v) => (pct ? `${v}%` : axisINR(v))} domain={pct ? [0, 100] : [0, "auto"]} />
          {hasRight && <YAxis yAxisId="right" orientation="right" tick={tick} stroke={colors.grid} width={56} tickFormatter={axisINR} />}
          <Tooltip content={tooltip || <ChartTooltip mode={mode} loanNames={loanNames} events={markers.events} display={settings.display} drawn={drawn} />}
                   cursor={{ fill: colors.grid, fillOpacity: 0.25 }} />
          {drawn.flatMap((d) => renderSeries(settings.chartType === "combo" ? "stacked" : settings.chartType, d, data, uid, hidden, axisOf))}
          {markers.today && labels.has(markers.today) && (
            <ReferenceLine yAxisId="left" x={markers.today} stroke={colors.accent} strokeDasharray="3 3"
                           label={{ value: "Today", position: "insideTopLeft", fill: colors.accent, fontSize: 10 }} />
          )}
          {showCross && (
            <ReferenceLine yAxisId="left" x={markers.crossover.label} stroke="#2E9E6B" strokeWidth={1.5}
                           label={{ value: `Crossover · EMI ${markers.crossover.month}`, position: "top", fill: "#2E9E6B", fontSize: 10 }} />
          )}
          {firstKey && markers.events.filter((e) => labels.has(e.label)).map((ev) => {
            const row = data.find((r) => r.label === ev.label);
            const key = mode === "split" ? `L${ev.loanIdx}` : firstKey;
            return (
              <ReferenceDot key={ev.text} yAxisId="left" x={ev.label} y={row?.[`v_${key}`] ?? 0}
                            shape={(p) => <EventDot {...p} ev={ev} />} ifOverflow="extendDomain" />
            );
          })}
          {drag.a && drag.b && <ReferenceArea yAxisId="left" x1={drag.a} x2={drag.b} fill={colors.accent} fillOpacity={0.15} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
